import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators';
import store from '@/store';
import { UrlUtil } from '@xl/js-utils';
import { welfareAndExitReason } from '@/common/safe-data';
import { CoupeSDK } from '@xl/coupe-sdk';
import ua from '@xl/user-agent';
import { DRIVER_WX_MINI_CLITENT_TYPE } from '@/config';
import { RegisterModule } from './register';

const {
  is_xl_driver_app: isInApp,
  isInWeixinMini,
  is_xl_user_app,
} = ua.parse();

export interface IAppState {
  urlParams: Record<string, string> | null;
  revision: number;
  isInApp: boolean;
  coupeData: Record<string, string | string[]>;
  wxConfigReady: boolean;
  isInDriverWxMini: boolean;
  welfareConfig: Record<string, Record<string, string>>;
  isInUserApp: boolean;
  pageShow: boolean;
  // 货运导流过来的场景
  isInHllDriver: boolean;
}

@Module({ dynamic: true, store, name: 'app' })
class App extends VuexModule implements IAppState {
  public urlParams: Record<string, string> | null = null;
  public revision = 0;
  public isInApp = false;
  public coupeData: Record<string, any> = welfareAndExitReason;
  public wxConfigReady = false;
  public isInDriverWxMini = false;
  public isInUserApp = false;
  public pageShow = true;
  public isInHllDriver = false;
  public token = '';

  get welfareConfig(): Record<string, Record<string, string>> {
    const welfareConfig =
      JSON.parse((this.coupeData.welfareConfig as string) || '{}') || {};
    return (
      welfareConfig[RegisterModule.registerCityObj.cityId || ''] ||
      welfareConfig.default
    );
  }

  @Mutation
  SET_URL_PARAMS(urlParameters: Record<string, string>) {
    this.urlParams = urlParameters;
  }

  @Mutation
  private SET_REVISION(revision: number) {
    this.revision = revision;
  }

  @Mutation
  private SET_IS_IN_APP(isInApp: boolean) {
    this.isInApp = isInApp;
  }

  @Mutation
  private SET_IS_IN_DRIVER_WX_MINI(isInDriverWxMini: boolean) {
    this.isInDriverWxMini = isInDriverWxMini;
  }

  @Mutation
  private SET_COUPE_DATA(data: Record<string, string | string[]>) {
    this.coupeData = data;
  }

  @Mutation
  private SET_WX_CONFIG_READY(ready: boolean) {
    this.wxConfigReady = ready;
  }

  @Mutation
  private SET_IS_IN_USER_APP(isInUserApp: boolean) {
    this.isInUserApp = isInUserApp;
  }

  @Mutation
  private SET_PAGE_SHOW(pageShow: boolean) {
    this.pageShow = pageShow;
  }

  @Mutation
  private SET_IS_IN_HLL_DRIVER(isInHllDriver: boolean) {
    this.isInHllDriver = isInHllDriver;
  }

  @Mutation
  SET_TOKEN(token: string) {
    this.token = token;
  }

  @Action
  public async GetCoupeData() {
    // 司机注册配置
    const sdk = new CoupeSDK({
      coupeId: 'cc86417400bb4d2bb1358f124273f3be',
    });

    sdk.setConfig({
      fetchMode: 'script',
    });

    const data = (await sdk.fetch()) as Record<string, string | string[]>;
    this.SET_COUPE_DATA(data);
  }

  @Action
  public InitAppStore() {
    const allUrlParams = UrlUtil.queryParse(window.location.href);
    const { revision, hll_driver_id } = allUrlParams;

    // NOTE: 注册页投放到乘客端内，ref 会被乘客 APP 包的 ref 覆盖
    // 投放到乘客端内的注册页，会约定使用 driverref 来作为渠道标识
    // 20220610 补充：为配合活动页链接中的 ref 被 APP 公参覆盖问题，APP 下个版本中将公参改名为 app_ref
    allUrlParams.ref =
      allUrlParams.driverref || allUrlParams.ref || allUrlParams.app_ref;

    this.SET_URL_PARAMS(allUrlParams);

    this.SET_IS_IN_APP(!!isInApp);
    this.SET_IS_IN_USER_APP(!!is_xl_user_app);

    this.SET_IS_IN_DRIVER_WX_MINI(
      !!(
        isInWeixinMini &&
        allUrlParams.client_type === DRIVER_WX_MINI_CLITENT_TYPE
      ),
    );

    // 货运跳转会携带hll_driver_id
    this.SET_IS_IN_HLL_DRIVER(!!hll_driver_id);

    if (revision) {
      this.SET_REVISION(+revision);
    }

    this.GetCoupeData();
  }

  @Action
  public SetWxConfigReady(ready: boolean) {
    this.SET_WX_CONFIG_READY(ready);
  }

  @Action
  public SetPageShow(pageShow: boolean) {
    this.SET_PAGE_SHOW(pageShow);
  }
}

export const AppModule = getModule(App);
